import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import {
  ArrowsRightLeftIcon,
  UserGroupIcon,
  PlusIcon,
  XMarkIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import ToggleSwitch from '../components/ToggleSwitch';

interface OwnershipTransferRestrictions {
  enabled: boolean;
  allow_only_company_users: boolean;
  allowed_domains: string[];
}

interface RecipientRestrictions {
  enabled: boolean;
  allow_only_company_users: boolean;
  allowed_domains: string[];
}

interface WhenUserLeavesSettings {
  transfer_ownership_to_admin_on_user_leave: boolean;
  allow_key_backup_on_self_deletion: boolean;
}

interface KeysBackupStrategy {
  when_user_leaves?: WhenUserLeavesSettings;
  allow_file_key_download: boolean;
  allow_multiple_file_key_download: boolean;
}

interface AssetProtectionSettings {
  ownership_transfer_restrictions?: OwnershipTransferRestrictions;
  recipient_restrictions?: RecipientRestrictions;
  keys_backup_strategy?: KeysBackupStrategy;
}

const AdminAssetProtectionSettings: React.FC = () => {
  const { t } = useTranslation('adminAssetProtection');
  usePageTitle(t('title'));
  const { user } = useAuth();
  const { setOnSave, setIsSubmitting, setHasUnsavedChanges } = useUnsavedChanges();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Original settings for change detection
  const [originalSettings, setOriginalSettings] = useState<{
    ownershipTransferRestrictions: OwnershipTransferRestrictions;
    recipientRestrictions: RecipientRestrictions;
    keysBackupStrategy: KeysBackupStrategy;
  } | null>(null);

  // Ownership transfer restrictions state
  const [ownershipTransferRestrictions, setOwnershipTransferRestrictions] = useState<OwnershipTransferRestrictions>({
    enabled: false,
    allow_only_company_users: false,
    allowed_domains: [],
  });

  // Recipient restrictions state
  const [recipientRestrictions, setRecipientRestrictions] = useState<RecipientRestrictions>({
    enabled: false,
    allow_only_company_users: false,
    allowed_domains: [],
  });

  // Keys backup strategy state (when_user_leaves + allow_file_key_download + allow_multiple_file_key_download)
  const [keysBackupStrategy, setKeysBackupStrategy] = useState<KeysBackupStrategy>({
    when_user_leaves: {
      transfer_ownership_to_admin_on_user_leave: false,
      allow_key_backup_on_self_deletion: false,
    },
    allow_file_key_download: false,
    allow_multiple_file_key_download: false,
  });

  // Domain input states
  const [newOwnershipDomain, setNewOwnershipDomain] = useState('');
  const [newRecipientDomain, setNewRecipientDomain] = useState('');
  const [domainSuggestions, setDomainSuggestions] = useState<string[]>([]);
  const [showOwnershipSuggestions, setShowOwnershipSuggestions] = useState(false);
  const [showRecipientSuggestions, setShowRecipientSuggestions] = useState(false);

  // Use refs to store the latest state so save handlers don't need them in dependencies
  const ownershipTransferRestrictionsRef = useRef(ownershipTransferRestrictions);
  const recipientRestrictionsRef = useRef(recipientRestrictions);
  const keysBackupStrategyRef = useRef(keysBackupStrategy);

  useEffect(() => {
    ownershipTransferRestrictionsRef.current = ownershipTransferRestrictions;
  }, [ownershipTransferRestrictions]);

  useEffect(() => {
    recipientRestrictionsRef.current = recipientRestrictions;
  }, [recipientRestrictions]);

  useEffect(() => {
    keysBackupStrategyRef.current = keysBackupStrategy;
  }, [keysBackupStrategy]);

  // Track if we're currently saving to prevent auto-saves
  const isSavingRef = useRef(false);

  // Load asset protection settings
  useEffect(() => {
    const loadData = async () => {
      if (!user?.company_id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const settings = await apiService.getCompanyAssetProtectionSettings(user.company_id);

        // Load domain suggestions
        try {
          const suggestions = await apiService.getDomainSuggestions(user.company_id);
          setDomainSuggestions(suggestions.domains || []);
        } catch (err) {
          console.error('Failed to load domain suggestions:', err);
          // Non-critical, continue without suggestions
        }

        // Set ownership transfer restrictions (default to disabled if not set)
        const ownershipTransfer = {
          enabled: settings.ownership_transfer_restrictions?.enabled ?? false,
          allow_only_company_users: settings.ownership_transfer_restrictions?.allow_only_company_users ?? false,
          allowed_domains: settings.ownership_transfer_restrictions?.allowed_domains || [],
        };
        setOwnershipTransferRestrictions(ownershipTransfer);

        // Set recipient restrictions (default to disabled if not set)
        const recipient = {
          enabled: settings.recipient_restrictions?.enabled ?? false,
          allow_only_company_users: settings.recipient_restrictions?.allow_only_company_users ?? false,
          allowed_domains: settings.recipient_restrictions?.allowed_domains || [],
        };
        setRecipientRestrictions(recipient);

        const kbs = settings.keys_backup_strategy ?? {
          when_user_leaves: { transfer_ownership_to_admin_on_user_leave: false, allow_key_backup_on_self_deletion: false },
          allow_file_key_download: false,
          allow_multiple_file_key_download: false,
        };
        const kbsState: KeysBackupStrategy = {
          when_user_leaves: {
            transfer_ownership_to_admin_on_user_leave: kbs.when_user_leaves?.transfer_ownership_to_admin_on_user_leave ?? false,
            allow_key_backup_on_self_deletion: kbs.when_user_leaves?.allow_key_backup_on_self_deletion ?? false,
          },
          allow_file_key_download: kbs.allow_file_key_download ?? false,
          allow_multiple_file_key_download: kbs.allow_multiple_file_key_download ?? false,
        };
        setKeysBackupStrategy(kbsState);

        // Store original settings for change detection
        setOriginalSettings({
          ownershipTransferRestrictions: { ...ownershipTransfer },
          recipientRestrictions: { ...recipient },
          keysBackupStrategy: { ...kbsState },
        });
      } catch (err: any) {
        console.error('Failed to load asset protection settings:', err);
        setError(t('errors.loadFailed'));
        toast.error(t('toast.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.company_id, t]);

  const handleSave = useCallback(async () => {
    if (!user?.company_id || isSavingRef.current) return;

    try {
      isSavingRef.current = true;
      setIsSubmitting(true);
      setError(null);

      const settingsToSave: AssetProtectionSettings = {
        ownership_transfer_restrictions: ownershipTransferRestrictionsRef.current,
        recipient_restrictions: recipientRestrictionsRef.current,
        keys_backup_strategy: keysBackupStrategyRef.current,
      };

      await apiService.updateCompanyAssetProtectionSettings(user.company_id, settingsToSave);

      // Reload data from server to ensure we have the latest persisted state
      const freshSettings = await apiService.getCompanyAssetProtectionSettings(user.company_id);

      // Update state with fresh data from server
      if (freshSettings.ownership_transfer_restrictions) {
        const updated = {
          enabled: freshSettings.ownership_transfer_restrictions.enabled ?? false,
          allow_only_company_users: freshSettings.ownership_transfer_restrictions.allow_only_company_users ?? false,
          allowed_domains: freshSettings.ownership_transfer_restrictions.allowed_domains || [],
        };
        setOwnershipTransferRestrictions(updated);
        ownershipTransferRestrictionsRef.current = updated;
      }
      if (freshSettings.recipient_restrictions) {
        const updated = {
          enabled: freshSettings.recipient_restrictions.enabled ?? false,
          allow_only_company_users: freshSettings.recipient_restrictions.allow_only_company_users ?? false,
          allowed_domains: freshSettings.recipient_restrictions.allowed_domains || [],
        };
        setRecipientRestrictions(updated);
        recipientRestrictionsRef.current = updated;
      }
      if (freshSettings.keys_backup_strategy) {
        const kbs = freshSettings.keys_backup_strategy;
        const updated: KeysBackupStrategy = {
          when_user_leaves: {
            transfer_ownership_to_admin_on_user_leave: kbs.when_user_leaves?.transfer_ownership_to_admin_on_user_leave ?? false,
            allow_key_backup_on_self_deletion: kbs.when_user_leaves?.allow_key_backup_on_self_deletion ?? false,
          },
          allow_file_key_download: kbs.allow_file_key_download ?? false,
          allow_multiple_file_key_download: kbs.allow_multiple_file_key_download ?? false,
        };
        setKeysBackupStrategy(updated);
        keysBackupStrategyRef.current = updated;
      }

      // Update original settings after successful save
      setOriginalSettings({
        ownershipTransferRestrictions: { ...ownershipTransferRestrictionsRef.current },
        recipientRestrictions: { ...recipientRestrictionsRef.current },
        keysBackupStrategy: { ...keysBackupStrategyRef.current },
      });

      toast.success(t('toast.saveSuccess'));
    } catch (err: any) {
      console.error('Failed to save asset protection settings:', err);
      setError(t('errors.saveFailed'));
      toast.error(err.response?.data?.error || t('toast.saveFailed'));
    } finally {
      setIsSubmitting(false);
      isSavingRef.current = false;
    }
  }, [user?.company_id, setIsSubmitting, setError, t]);

  // Check if there are meaningful changes
  const hasMeaningfulChanges = useCallback(() => {
    if (!originalSettings) return false;

    const current = {
      ownershipTransferRestrictions,
      recipientRestrictions,
      keysBackupStrategy,
    };

    // Check ownership transfer restrictions
    // If enabled, must be properly configured (company-only OR domains)
    if (current.ownershipTransferRestrictions.enabled) {
      const isConfigured = current.ownershipTransferRestrictions.allow_only_company_users || current.ownershipTransferRestrictions.allowed_domains.length > 0;
      if (!isConfigured) {
        // Enabled but not configured - invalid state, don't show save button
        return false;
      }
    }

    // Check recipient restrictions
    // If enabled, must be properly configured (company-only OR domains)
    if (current.recipientRestrictions.enabled) {
      const isConfigured = current.recipientRestrictions.allow_only_company_users || current.recipientRestrictions.allowed_domains.length > 0;
      if (!isConfigured) {
        // Enabled but not configured - invalid state, don't show save button
        return false;
      }
    }

    // Now check if there are actual changes from original
    const otEnabledChanged = current.ownershipTransferRestrictions.enabled !== originalSettings.ownershipTransferRestrictions.enabled;
    const otCompanyOnlyChanged = current.ownershipTransferRestrictions.allow_only_company_users !== originalSettings.ownershipTransferRestrictions.allow_only_company_users;
    const otDomainsChanged = JSON.stringify([...current.ownershipTransferRestrictions.allowed_domains].sort()) !== JSON.stringify([...originalSettings.ownershipTransferRestrictions.allowed_domains].sort());
    const otChanged = otEnabledChanged || otCompanyOnlyChanged || otDomainsChanged;

    const recEnabledChanged = current.recipientRestrictions.enabled !== originalSettings.recipientRestrictions.enabled;
    const recCompanyOnlyChanged = current.recipientRestrictions.allow_only_company_users !== originalSettings.recipientRestrictions.allow_only_company_users;
    const recDomainsChanged = JSON.stringify([...current.recipientRestrictions.allowed_domains].sort()) !== JSON.stringify([...originalSettings.recipientRestrictions.allowed_domains].sort());
    const recChanged = recEnabledChanged || recCompanyOnlyChanged || recDomainsChanged;

    const kbs = originalSettings.keysBackupStrategy;
    const kbsTransfer = kbs && (current.keysBackupStrategy.when_user_leaves?.transfer_ownership_to_admin_on_user_leave !== kbs.when_user_leaves?.transfer_ownership_to_admin_on_user_leave);
    const kbsBackup = kbs && (current.keysBackupStrategy.when_user_leaves?.allow_key_backup_on_self_deletion !== kbs.when_user_leaves?.allow_key_backup_on_self_deletion);
    const kbsFile = kbs && (current.keysBackupStrategy.allow_file_key_download !== kbs.allow_file_key_download);
    const kbsMulti = kbs && (current.keysBackupStrategy.allow_multiple_file_key_download !== kbs.allow_multiple_file_key_download);
    const kbsChanged = Boolean(kbsTransfer || kbsBackup || kbsFile || kbsMulti);

    return otChanged || recChanged || kbsChanged;
  }, [ownershipTransferRestrictions, recipientRestrictions, keysBackupStrategy, originalSettings]);

  // Set up save handler and track unsaved changes
  useEffect(() => {
    const hasChanges = hasMeaningfulChanges();
    setHasUnsavedChanges(hasChanges);
    
    if (hasChanges) {
      setOnSave(() => handleSave);
    } else {
      setOnSave(null);
    }
    
    return () => setOnSave(null);
  }, [hasMeaningfulChanges, setHasUnsavedChanges, setOnSave, handleSave]);

  // Handle ownership transfer restrictions toggle
  const handleOwnershipTransferEnabled = (enabled: boolean) => {
    setOwnershipTransferRestrictions((prev) => ({
      ...prev,
      enabled,
      // If disabling, reset to defaults
      ...(enabled ? {} : { allow_only_company_users: false, allowed_domains: [] }),
    }));
  };

  // Handle ownership transfer company-only toggle
  const handleOwnershipTransferCompanyOnly = (allowOnlyCompanyUsers: boolean) => {
    setOwnershipTransferRestrictions((prev) => ({
      ...prev,
      allow_only_company_users: allowOnlyCompanyUsers,
      // If switching to company-only, clear allowed domains
      allowed_domains: allowOnlyCompanyUsers ? [] : prev.allowed_domains,
    }));
  };

  // Handle recipient restrictions toggle
  const handleRecipientEnabled = (enabled: boolean) => {
    setRecipientRestrictions((prev) => ({
      ...prev,
      enabled,
      // If disabling, reset to defaults
      ...(enabled ? {} : { allow_only_company_users: false, allowed_domains: [] }),
    }));
  };

  // Handle recipient company-only toggle
  const handleRecipientCompanyOnly = (allowOnlyCompanyUsers: boolean) => {
    setRecipientRestrictions((prev) => ({
      ...prev,
      allow_only_company_users: allowOnlyCompanyUsers,
      // If switching to company-only, clear allowed domains
      allowed_domains: allowOnlyCompanyUsers ? [] : prev.allowed_domains,
    }));
  };

  // Check for common domain typos
  const checkDomainTypo = (domain: string): string | null => {
    const typos: Record<string, string> = {
      'gmail.co': 'gmail.com',
      'gmial.com': 'gmail.com',
      'gmail.cm': 'gmail.com',
      'gmail.con': 'gmail.com',
      'yahoo.co': 'yahoo.com',
      'yahoo.cm': 'yahoo.com',
      'outlook.co': 'outlook.com',
      'outlook.cm': 'outlook.com',
      'hotmail.co': 'hotmail.com',
      'hotmail.cm': 'hotmail.com',
      'icloud.co': 'icloud.com',
      'icloud.cm': 'icloud.com',
      'protonmail.co': 'protonmail.com',
      'protonmail.cm': 'protonmail.com',
    };
    return typos[domain] || null;
  };

  // Validate domain format
  const validateDomainFormat = (domain: string): { valid: boolean; error?: string } => {
    if (!domain) {
      return { valid: false, error: t('validation.domainEmpty') };
    }

    // Basic domain validation
    const domainRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
    if (!domainRegex.test(domain)) {
      return { valid: false, error: t('validation.domainInvalid') };
    }

    if (domain.length > 253) {
      return { valid: false, error: t('validation.domainTooLong') };
    }

    const parts = domain.split('.');
    if (parts.length < 2) {
      return { valid: false, error: t('validation.domainOneDot') };
    }

    for (const part of parts) {
      if (part.length === 0) {
        return { valid: false, error: t('validation.domainEmptyParts') };
      }
      if (part.length > 63) {
        return { valid: false, error: t('validation.domainPartTooLong') };
      }
      if (part.startsWith('-') || part.endsWith('-')) {
        return { valid: false, error: t('validation.domainHyphen') };
      }
    }

    return { valid: true };
  };

  // Add ownership transfer domain
  const handleAddOwnershipDomain = () => {
    const domain = newOwnershipDomain.trim().toLowerCase().replace(/^@/, '');
    if (!domain) return;

    // Check for typos
    const corrected = checkDomainTypo(domain);
    if (corrected) {
      toast.error(t('toast.domainTypo', { domain: corrected }), { duration: 4000 });
      return;
    }

    // Validate domain format
    const validation = validateDomainFormat(domain);
    if (!validation.valid) {
      toast.error(validation.error || t('validation.domainInvalid'));
      return;
    }

    if (ownershipTransferRestrictions.allowed_domains.includes(domain)) {
      toast.error(t('toast.domainAlreadyInList'));
      return;
    }

    setOwnershipTransferRestrictions((prev) => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, domain],
    }));
    setNewOwnershipDomain('');
    setShowOwnershipSuggestions(false);
  };

  // Remove ownership transfer domain
  const handleRemoveOwnershipDomain = (domain: string) => {
    setOwnershipTransferRestrictions((prev) => {
      const newDomains = prev.allowed_domains.filter((d) => d !== domain);
      // If no domains left and company-only is not enabled, disable the restriction
      const shouldDisable = newDomains.length === 0 && !prev.allow_only_company_users && prev.enabled;
      return {
        ...prev,
        allowed_domains: newDomains,
        enabled: shouldDisable ? false : prev.enabled,
      };
    });
  };

  // Add recipient domain
  const handleAddRecipientDomain = () => {
    const domain = newRecipientDomain.trim().toLowerCase().replace(/^@/, '');
    if (!domain) return;

    // Check for typos
    const corrected = checkDomainTypo(domain);
    if (corrected) {
      toast.error(t('toast.domainTypo', { domain: corrected }), { duration: 4000 });
      return;
    }

    // Validate domain format
    const validation = validateDomainFormat(domain);
    if (!validation.valid) {
      toast.error(validation.error || t('validation.domainInvalid'));
      return;
    }

    if (recipientRestrictions.allowed_domains.includes(domain)) {
      toast.error(t('toast.domainAlreadyInList'));
      return;
    }

    setRecipientRestrictions((prev) => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, domain],
    }));
    setNewRecipientDomain('');
    setShowRecipientSuggestions(false);
  };

  // Remove recipient domain
  const handleRemoveRecipientDomain = (domain: string) => {
    setRecipientRestrictions((prev) => {
      const newDomains = prev.allowed_domains.filter((d) => d !== domain);
      // If no domains left and company-only is not enabled, disable the restriction
      const shouldDisable = newDomains.length === 0 && !prev.allow_only_company_users && prev.enabled;
      return {
        ...prev,
        allowed_domains: newDomains,
        enabled: shouldDisable ? false : prev.enabled,
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        {/* Ownership Transfer Restrictions */}
        <div className="mb-8 pb-8 border-b border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <ArrowsRightLeftIcon className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('ownershipTransfer.title')}</h3>
          </div>

          <div className="space-y-4">
            {/* Enable restrictions toggle */}
            <div className="flex items-center justify-between py-3">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('ownershipTransfer.enableRestrictions')}</span>
                <p className="text-xs text-gray-500 mt-1">
                  {t('ownershipTransfer.enableDescription')}
                </p>
              </div>
              <ToggleSwitch
                checked={ownershipTransferRestrictions.enabled}
                onChange={handleOwnershipTransferEnabled}
              />
            </div>

            {ownershipTransferRestrictions.enabled && (
              <>
                {/* Company-only toggle */}
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('ownershipTransfer.companyUsersOnly')}</span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('ownershipTransfer.companyUsersOnlyDescription')}
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={ownershipTransferRestrictions.allow_only_company_users}
                    onChange={handleOwnershipTransferCompanyOnly}
                  />
                </div>

                {!ownershipTransferRestrictions.allow_only_company_users && (
                  <div className="mt-4 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('ownershipTransfer.allowedDomainsLabel')}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      {t('ownershipTransfer.allowedDomainsDescription')}
                    </p>
                    <div className="flex gap-2 relative">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={newOwnershipDomain}
                          onChange={(e) => {
                            setNewOwnershipDomain(e.target.value);
                            setShowOwnershipSuggestions(e.target.value.length > 0);
                          }}
                          onFocus={() => setShowOwnershipSuggestions(newOwnershipDomain.length > 0 && domainSuggestions.length > 0)}
                          onBlur={() => setTimeout(() => setShowOwnershipSuggestions(false), 200)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddOwnershipDomain();
                            }
                          }}
                          placeholder={t('placeholder.domain')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {showOwnershipSuggestions && domainSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {domainSuggestions
                              .filter((d) => !ownershipTransferRestrictions.allowed_domains.includes(d))
                              .filter((d) => !newOwnershipDomain || d.toLowerCase().includes(newOwnershipDomain.toLowerCase()))
                              .slice(0, 10)
                              .map((domain) => (
                                <button
                                  key={domain}
                                  type="button"
                                  onClick={() => {
                                    setNewOwnershipDomain(domain);
                                    setShowOwnershipSuggestions(false);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                                >
                                  {domain}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleAddOwnershipDomain}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                      >
                        <PlusIcon className="w-4 h-4" />
                        {t('add')}
                      </button>
                    </div>
                    {ownershipTransferRestrictions.allowed_domains.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {ownershipTransferRestrictions.allowed_domains.map((domain) => (
                          <span
                            key={domain}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {domain}
                            <button
                              onClick={() => handleRemoveOwnershipDomain(domain)}
                              className="hover:text-blue-900"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Recipient Restrictions */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <UserGroupIcon className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('recipient.title')}</h3>
          </div>

          <div className="space-y-4">
            {/* Enable restrictions toggle */}
            <div className="flex items-center justify-between py-3">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('recipient.enableRestrictions')}</span>
                <p className="text-xs text-gray-500 mt-1">
                  {t('recipient.enableDescription')}
                </p>
              </div>
              <ToggleSwitch
                checked={recipientRestrictions.enabled}
                onChange={handleRecipientEnabled}
              />
            </div>

            {recipientRestrictions.enabled && (
              <>
                {/* Company-only toggle */}
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('recipient.companyUsersOnly')}</span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('recipient.companyUsersOnlyDescription')}
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={recipientRestrictions.allow_only_company_users}
                    onChange={handleRecipientCompanyOnly}
                  />
                </div>

                {!recipientRestrictions.allow_only_company_users && (
                  <div className="mt-4 space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('recipient.allowedDomainsLabel')}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      {t('recipient.allowedDomainsDescription')}
                    </p>
                    <div className="flex gap-2 relative">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={newRecipientDomain}
                          onChange={(e) => {
                            setNewRecipientDomain(e.target.value);
                            setShowRecipientSuggestions(e.target.value.length > 0);
                          }}
                          onFocus={() => setShowRecipientSuggestions(newRecipientDomain.length > 0 && domainSuggestions.length > 0)}
                          onBlur={() => setTimeout(() => setShowRecipientSuggestions(false), 200)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddRecipientDomain();
                            }
                          }}
                          placeholder={t('placeholder.domain')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {showRecipientSuggestions && domainSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {domainSuggestions
                              .filter((d) => !recipientRestrictions.allowed_domains.includes(d))
                              .filter((d) => !newRecipientDomain || d.toLowerCase().includes(newRecipientDomain.toLowerCase()))
                              .slice(0, 10)
                              .map((domain) => (
                                <button
                                  key={domain}
                                  type="button"
                                  onClick={() => {
                                    setNewRecipientDomain(domain);
                                    setShowRecipientSuggestions(false);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm"
                                >
                                  {domain}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleAddRecipientDomain}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                      >
                        <PlusIcon className="w-4 h-4" />
                        {t('add')}
                      </button>
                    </div>
                    {recipientRestrictions.allowed_domains.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {recipientRestrictions.allowed_domains.map((domain) => (
                          <span
                            key={domain}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {domain}
                            <button
                              onClick={() => handleRemoveRecipientDomain(domain)}
                              className="hover:text-blue-900"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Keys backup strategy (When users leave + File key download) */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <ArchiveBoxIcon className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('backups.title')}</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">{t('backups.subtitle')}</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('backups.transferToAdmin')}</span>
                <p className="text-xs text-gray-500 mt-1">{t('backups.transferToAdminDescription')}</p>
              </div>
              <ToggleSwitch
                checked={keysBackupStrategy.when_user_leaves?.transfer_ownership_to_admin_on_user_leave ?? false}
                onChange={(checked) => setKeysBackupStrategy((prev) => ({
                  ...prev,
                  when_user_leaves: {
                    ...(prev.when_user_leaves ?? { transfer_ownership_to_admin_on_user_leave: false, allow_key_backup_on_self_deletion: false }),
                    transfer_ownership_to_admin_on_user_leave: checked,
                  },
                }))}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('backups.allowKeyBackup')}</span>
                <p className="text-xs text-gray-500 mt-1">{t('backups.allowKeyBackupDescription')}</p>
              </div>
              <ToggleSwitch
                checked={keysBackupStrategy.when_user_leaves?.allow_key_backup_on_self_deletion ?? false}
                onChange={(checked) => setKeysBackupStrategy((prev) => ({
                  ...prev,
                  when_user_leaves: {
                    ...(prev.when_user_leaves ?? { transfer_ownership_to_admin_on_user_leave: false, allow_key_backup_on_self_deletion: false }),
                    allow_key_backup_on_self_deletion: checked,
                  },
                }))}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('backups.allowFileKeyDownload')}</span>
                <p className="text-xs text-gray-500 mt-1">{t('backups.allowFileKeyDownloadDescription')}</p>
              </div>
              <ToggleSwitch
                checked={keysBackupStrategy.allow_file_key_download}
                disabled={keysBackupStrategy.allow_multiple_file_key_download}
                onChange={(checked) => setKeysBackupStrategy((prev) => ({ ...prev, allow_file_key_download: checked }))}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('backups.allowMultipleFileKeyDownload')}</span>
                <p className="text-xs text-gray-500 mt-1">{t('backups.allowMultipleFileKeyDownloadDescription')}</p>
              </div>
              <ToggleSwitch
                checked={keysBackupStrategy.allow_multiple_file_key_download}
                onChange={(checked) => setKeysBackupStrategy((prev) => ({
                  ...prev,
                  allow_multiple_file_key_download: checked,
                  allow_file_key_download: checked ? true : prev.allow_file_key_download,
                }))}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAssetProtectionSettings;
